import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Briefcase, Home, Users, ArrowRight, ArrowLeft, Sparkles, User, Calendar as CalendarIcon, Clock, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { DateInput, isValidDate, formatISO } from "./DateInput";

const RELATIONSHIP_OPTIONS = [
  { v: "romantic", l: "Romantic",  Icon: Heart,     hint: "Partners, lovers, dating" },
  { v: "business", l: "Business",  Icon: Briefcase, hint: "Co-founders, colleagues" },
  { v: "family",   l: "Family",    Icon: Home,      hint: "Parent, sibling, in-law" },
  { v: "friends",  l: "Friendship", Icon: Users,    hint: "Best friend, close circle" },
];

const emptyPerson = () => ({ name: "", dob: { d: "", m: "", y: "" }, tob: "", pob: "" });

const canContinuePerson = (p) =>
  p.name.trim().length >= 2 &&
  isValidDate(p.dob) &&
  (p.tob === "" || /^\d{2}:\d{2}$/.test(p.tob)) &&
  p.pob.trim().length >= 2;

const PersonPanel = ({ label, person, onChange, testPrefix }) => {
  return (
    <div className="space-y-4">
      <p className="text-stardust/70 uppercase tracking-[0.35em] text-[10px]" data-testid={`${testPrefix}-heading`}>{label}</p>

      <div>
        <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
          <User className="h-3 w-3 text-stardust" /> Full name
        </Label>
        <Input
          data-testid={`${testPrefix}-name`}
          value={person.name}
          onChange={(e) => onChange({ ...person, name: e.target.value })}
          placeholder="e.g. Rhea Kapoor"
          className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-stardust focus-visible:border-stardust"
        />
      </div>

      <div>
        <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
          <CalendarIcon className="h-3 w-3 text-stardust" /> Date of birth
        </Label>
        <DateInput value={person.dob} onChange={(dob) => onChange({ ...person, dob })} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
            <Clock className="h-3 w-3 text-stardust" /> Time <span className="normal-case text-white/40 tracking-normal">(optional)</span>
          </Label>
          <Input
            data-testid={`${testPrefix}-tob`}
            type="time"
            value={person.tob}
            onChange={(e) => onChange({ ...person, tob: e.target.value })}
            className="bg-black/30 border-white/10 text-white h-11 focus-visible:ring-stardust focus-visible:border-stardust [color-scheme:dark]"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
            <MapPin className="h-3 w-3 text-stardust" /> Place of birth
          </Label>
          <Input
            data-testid={`${testPrefix}-pob`}
            value={person.pob}
            onChange={(e) => onChange({ ...person, pob: e.target.value })}
            placeholder="City, Country"
            className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-stardust focus-visible:border-stardust"
          />
        </div>
      </div>
    </div>
  );
};

export const PairPredictionForm = ({ onSubmit, onBack }) => {
  const [step, setStep] = useState(0);
  const [rtype, setRtype] = useState("romantic");
  const [personA, setPersonA] = useState(emptyPerson());
  const [personB, setPersonB] = useState(emptyPerson());

  const canContinue = () => {
    if (step === 0) return !!rtype;
    if (step === 1) return canContinuePerson(personA);
    if (step === 2) return canContinuePerson(personB);
    return false;
  };

  const next = () => {
    if (!canContinue()) return;
    if (step < 2) setStep(step + 1);
    else {
      onSubmit({
        relationship_type: rtype,
        person_a: {
          name: personA.name.trim(),
          date_of_birth: formatISO(personA.dob),
          time_of_birth: personA.tob || null,
          place_of_birth: personA.pob.trim(),
        },
        person_b: {
          name: personB.name.trim(),
          date_of_birth: formatISO(personB.dob),
          time_of_birth: personB.tob || null,
          place_of_birth: personB.pob.trim(),
        },
      });
    }
  };

  const prev = () => {
    if (step === 0) onBack?.();
    else setStep(step - 1);
  };

  const headings = [
    "What kind of pairing?",
    `Tell us about ${personA.name.trim() || "the first person"}.`,
    `Now ${personB.name.trim() || "the second person"}.`,
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl"
      >
        <div className="glass rounded-2xl p-6 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-2 mb-6 sm:mb-8" data-testid="pair-progress">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={cn("h-[2px] flex-1 rounded-full transition-all duration-500", i <= step ? "bg-stardust" : "bg-white/10")}
              />
            ))}
          </div>

          <div className="mb-6 sm:mb-8">
            <p className="text-stardust/80 uppercase tracking-[0.3em] text-[10px] mb-3">Step {step + 1} of 3</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-[#F5EBD8] leading-tight">
              {headings[step]}
            </h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rtype-chips">
                  {RELATIONSHIP_OPTIONS.map(({ v, l, Icon, hint }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setRtype(v)}
                      data-testid={`rtype-${v}`}
                      className={cn(
                        "text-left px-4 py-4 rounded-xl border transition-all group",
                        rtype === v
                          ? "bg-stardust/10 border-stardust text-stardust"
                          : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-stardust/50 hover:text-stardust"
                      )}
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <Icon className="h-4 w-4" />
                        <span className="uppercase tracking-[0.2em] text-[11px] font-medium">{l}</span>
                      </div>
                      <p className="text-xs text-slate-400 group-hover:text-slate-300 leading-relaxed">{hint}</p>
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <PersonPanel
                  label="Person one"
                  person={personA}
                  onChange={setPersonA}
                  testPrefix="pair-a"
                />
              )}

              {step === 2 && (
                <PersonPanel
                  label="Person two"
                  person={personB}
                  onChange={setPersonB}
                  testPrefix="pair-b"
                />
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8 sm:mt-10 gap-3">
            <Button
              data-testid="pair-back-button"
              variant="ghost"
              onClick={prev}
              className="text-slate-400 hover:text-white hover:bg-white/5 px-3 sm:px-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              data-testid="pair-next-button"
              onClick={next}
              disabled={!canContinue()}
              className="bg-stardust text-cosmic-900 hover:bg-stardust-light hover:shadow-[0_0_24px_rgba(226,194,117,0.4)] disabled:opacity-40 disabled:hover:shadow-none rounded-full px-5 sm:px-6 py-5 font-medium transition-all"
            >
              {step === 2 ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Draw the pair chart
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
